import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import type { DatosCategoriaMes } from './objetivos';

/**
 * «Pagadas»: categorías ya ejecutadas este mes (Hipoteca, Seguro, Visa…),
 * que no volverán hasta el mes que viene. Funciones puras.
 *
 * - Automática: categoría marcada como «pago fijo mensual» (pref
 *   `pagos-fijos`) con gasto este mes y casi nada por gastar: saldo ≤
 *   max(1 €, 2 % de lo asignado), o gasto ≥ 95 % del objetivo del mes (o de
 *   lo asignado).
 * - Manual: «Marcar como pagada este mes» (pref `pagadas-manuales`,
 *   `{ mes: [ids] }`), también para las que no son pago fijo.
 * - «Desmarcar este mes» (no pagada a mano): una automática que no lo está
 *   de verdad; se guarda en el mismo mes como `!id` y gana a la detección.
 * - El gasto de más manda: una categoría en negativo nunca está pagada.
 */

/** Saldo despreciable: hasta 1 € … */
export const MINIMO_RESTO = 100;
/** … o hasta el 2 % de lo asignado. */
export const FRACCION_RESTO = 0.02;
/** Gasto que da por cumplido el mes (95 % del objetivo o de lo asignado). */
export const FRACCION_GASTO = 0.95;

export type MarcasPagadas = Record<string, string[]>;

/** Prefijo de la marca «no pagada este mes» dentro de `pagadas-manuales`. */
export const PREFIJO_NO_PAGADA = '!';

/** Marca manual de una categoría en un mes. */
export type MarcaManual = 'pagada' | 'no-pagada' | null;

/** La marca manual de `id` entre las del mes. */
export function marcaManual(
  manuales: readonly string[],
  id: string,
): MarcaManual {
  if (manuales.includes(`${PREFIJO_NO_PAGADA}${id}`)) {
    return 'no-pagada';
  }
  return manuales.includes(id) ? 'pagada' : null;
}

/** ¿Pagada automáticamente? (solo para pagos fijos). */
export function pagadaAutomatica(datos: DatosCategoriaMes): boolean {
  const gasto = -datos.spent;
  if (gasto <= 0 || datos.balance < 0) {
    return false;
  }
  const resto = Math.max(
    MINIMO_RESTO,
    Math.round(FRACCION_RESTO * Math.max(0, datos.budgeted)),
  );
  if (datos.balance <= resto) {
    return true;
  }
  const referencia: IntegerAmount =
    datos.goal != null && datos.goal > 0 ? datos.goal : datos.budgeted;
  return referencia > 0 && gasto >= FRACCION_GASTO * referencia;
}

/** ¿Pagada este mes? (fija y automática, o marcada a mano). */
export function esPagada({
  fijo,
  manual,
  noPagada = false,
  datos,
}: {
  fijo: boolean;
  manual: boolean;
  /** «Desmarcar este mes»: gana a la detección automática. */
  noPagada?: boolean;
  datos: DatosCategoriaMes | undefined;
}): boolean {
  if (noPagada || (datos && datos.balance < 0)) {
    return false;
  }
  if (manual) {
    return true;
  }
  return fijo && datos != null && pagadaAutomatica(datos);
}

/** Pref `pagos-fijos` (JSON array de ids), tolerante. */
export function parsePagosFijos(raw: string | null | undefined): Set<string> {
  try {
    const v: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(v)
        ? v.filter((x): x is string => typeof x === 'string')
        : [],
    );
  } catch {
    return new Set();
  }
}

/** Pref `pagadas-manuales` (`{ mes: [ids] }`), tolerante. */
export function parseMarcas(raw: string | null | undefined): MarcasPagadas {
  try {
    const v: unknown = raw ? JSON.parse(raw) : {};
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return {};
    }
    const r: MarcasPagadas = {};
    for (const [mes, ids] of Object.entries(v as Record<string, unknown>)) {
      if (/^\d{4}-\d{2}$/.test(mes) && Array.isArray(ids)) {
        r[mes] = ids.filter((x): x is string => typeof x === 'string');
      }
    }
    return r;
  } catch {
    return {};
  }
}

/** Meses de marcas manuales que se conservan (los anteriores se podan). */
export const MESES_MARCAS = 3;

/**
 * Pone la marca manual de una categoría en `mes` (`true`: pagada; `'no'`:
 * no pagada aunque se detecte; `false`: sin marca) y poda los meses
 * anteriores a `mesActual − MESES_MARCAS`.
 */
export function conMarca(
  marcas: MarcasPagadas,
  mes: string,
  id: string,
  pagada: boolean | 'no',
  mesActual: string,
): MarcasPagadas {
  const limite = monthUtils.subMonths(mesActual, MESES_MARCAS);
  const r: MarcasPagadas = {};
  for (const [m, ids] of Object.entries(marcas)) {
    if (m >= limite) {
      r[m] = ids;
    }
  }
  const actuales = new Set(r[mes] ?? []);
  const no = `${PREFIJO_NO_PAGADA}${id}`;
  actuales.delete(id);
  actuales.delete(no);
  if (pagada === 'no') {
    actuales.add(no);
  } else if (pagada) {
    actuales.add(id);
  }
  if (actuales.size > 0) {
    r[mes] = [...actuales];
  } else {
    delete r[mes];
  }
  return r;
}

/** Ids pagados en un mes. */
export function pagadasDelMes({
  datos,
  fijos,
  manuales,
}: {
  datos: ReadonlyMap<string, DatosCategoriaMes>;
  fijos: ReadonlySet<string>;
  manuales: readonly string[];
}): Set<string> {
  const marcadas = new Set(manuales);
  const r = new Set<string>();
  for (const [id, d] of datos) {
    if (
      esPagada({
        fijo: fijos.has(id),
        manual: marcadas.has(id),
        noPagada: marcadas.has(`${PREFIJO_NO_PAGADA}${id}`),
        datos: d,
      })
    ) {
      r.add(id);
    }
  }
  return r;
}

/**
 * Nombres de grupo que son de facturas (recibos que se pagan una vez al mes):
 * «Facturas», «Recibos», «Suministros», «Gastos fijos», «Bills»…
 */
const GRUPO_FACTURAS = /factur|recibo|suministr|fij[oa]s?\b|\bbills?\b/i;

/** ¿El grupo es de facturas? */
export function esGrupoFacturas(nombre: string | null | undefined): boolean {
  return nombre != null && GRUPO_FACTURAS.test(nombre);
}

/**
 * ¿Lleva la fila el círculo de «Pagado este mes»? (B4 · H31). Solo en los
 * grupos de facturas y en las categorías marcadas como pago fijo mensual; y
 * siempre que ya esté pagada, para poder desmarcarla.
 */
export function muestraCirculoPagada({
  nombreGrupo,
  fijo,
  pagada,
}: {
  nombreGrupo: string | null | undefined;
  fijo: boolean;
  pagada: boolean;
}): boolean {
  return pagada || fijo || esGrupoFacturas(nombreGrupo);
}
